import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap } from 'rxjs';

import { Vehicle } from '../pages/appointment/plan-appointment/license-plate-selection/interfaces/vehicle.interface';
import { BookingState, BookingStateService } from './booking-state.service';
import { LicensePlateService } from './license-plate.service';

/**
 * Persist merk/model uit vehicle-information naar `state.extra`, zodat GET /state (en de
 * wpp:state host-expose) ze altijd meeleveren. De backend accepteert een PATCH pas als er
 * een actieve plate is (gezet door POST /jobs) — vandaar twee varianten. Beide zijn
 * nooit-blokkerend: fouten worden geslikt, de flow mag hier nooit op stranden.
 */
@Injectable({ providedIn: 'root' })
export class VehicleExtraService {
  private readonly bookingState: BookingStateService = inject(BookingStateService);
  private readonly licensePlateService: LicensePlateService = inject(LicensePlateService);

  // Onvoorwaardelijke write — voor direct ná POST /jobs, als de actieve plate dit voertuig is.
  public persist(vehicle: Vehicle | null): Observable<unknown> {
    const manufacturer: string | null = vehicle?.manufacturer ?? null;
    const model: string | null = vehicle?.description ?? null;
    if (!manufacturer && !model) {
      return of(null);
    }
    return this.bookingState
      .patch({ extra: { manufacturer, model } })
      .pipe(catchError((): Observable<null> => of(null)));
  }

  // Klik-moment (vóór POST /jobs): alleen PATCHen als de actieve plate al dít kenteken is —
  // een verse journey heeft nog geen state (400) en een ándere actieve plate mag niet vervuild.
  public persistIfActivePlate(vehicle: Vehicle | null, licensePlate: string): Observable<unknown> {
    const normalized: string = this.licensePlateService.normalizeLicensePlate(licensePlate);
    return this.bookingState.peek().pipe(
      switchMap(
        (state: BookingState): Observable<unknown> =>
          state.licensePlate === normalized ? this.persist(vehicle) : of(null),
      ),
      catchError((): Observable<null> => of(null)),
    );
  }
}
